import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { UiButton, UiIcon } from 'ui-components';
import { toggleSidebarFilters } from 'store';
import { isUndefined } from 'lib';
import { StyledFiltersWrapper, StyledResetButton } from './addFilter.styles';
import { StyledBottomSheets, StyledDrawer } from './AddFilterDrawer.styles';
import FilterList from './FilterList';

function AddFilterDrawer({
  sliceModule,
  mapToComponent,
  onApply,
  onReset,
  onClose,
  allowedFilters,
}) {
  const { platform } = useParams();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const isOpenSidebarFilters = useSelector(
    (state) => state.filterReducer.isOpenSidebarFilters
  );
  const filters = useSelector(
    (state) => state[state.filterReducer.filtersContext]?.filters
  );
  const selectedFilter = useSelector(
    (state) => state[state.filterReducer.filtersContext]?.selectedFilter
  );

  const Component = mapToComponent[selectedFilter];
  const currentFilter = filters?.[selectedFilter];

  const clearSelectedFilter = () => {
    dispatch(sliceModule?.setSelectedFilter(undefined));
  };

  const toggleDrawer = () => {
    dispatch(toggleSidebarFilters(!isOpenSidebarFilters));
    clearSelectedFilter();
  };

  const renderFilterList = (
    <StyledFiltersWrapper className='wrapper'>
      <div className='header'>
        <UiIcon icon='icon ri-equalizer-line' size={20} />
        {t('filter:filterSettings')}
        {platform ? t(`common:${platform}`) : ''}
        <StyledResetButton
          variant='outlined'
          color='primary'
          startIcon={<UiIcon icon='icon ri-arrow-go-back-line ' size={20} />}
          onClick={onReset}
        >
          {t('common:undo')}
        </StyledResetButton>
      </div>
      <div className='body'>
        <FilterList
          mapToComponent={mapToComponent}
          sliceModule={sliceModule}
          allowedFilters={allowedFilters}
        />
      </div>

      <StyledBottomSheets>
        <UiButton variant='outlined' color='primary' onClick={onClose}>
          {t('common:close')}
        </UiButton>
        <UiButton
          variant='contained'
          color='primary'
          onClick={onApply}
          fullWidth={true}
        >
          {t('filter:applyFilters')}
        </UiButton>
      </StyledBottomSheets>
    </StyledFiltersWrapper>
  );

  const renderFilterDetails = (
    <StyledFiltersWrapper className='wrapper'>
      <div className='header'>
        <UiIcon icon='icon ri-equalizer-line' size={20} />
        {t(currentFilter?.title || currentFilter?.translateKey)}
        <UiButton
          className='back-button'
          variant='text'
          endIcon={<UiIcon icon='icon ri-arrow-left-line ' size={20} />}
          onClick={clearSelectedFilter}
        >
          {t('filter:backToFilters')}
        </UiButton>
      </div>
      <div className='body details'>
        {i18n.exists(`filter:${selectedFilter}Title`) && (
          <h1>{t(`filter:${selectedFilter}Title`)}</h1>
        )}
        {!isUndefined(Component) && (
          <Component componentName={selectedFilter} className='filter' />
        )}
      </div>
      <StyledBottomSheets>
        <UiButton
          variant='contained'
          color='primary'
          onClick={onApply}
          fullWidth={true}
        >
          {t('filter:saveChanges')}
        </UiButton>
      </StyledBottomSheets>
    </StyledFiltersWrapper>
  );

  return (
    <StyledDrawer open={isOpenSidebarFilters} onToggle={toggleDrawer}>
      {selectedFilter && renderFilterDetails}

      {!selectedFilter && renderFilterList}
    </StyledDrawer>
  );
}

AddFilterDrawer.propTypes = {
  sliceModule: PropTypes.object,
  mapToComponent: PropTypes.object,
  onApply: PropTypes.func,
  onClose: PropTypes.func,
  onReset: PropTypes.func,
  allowedFilters: PropTypes.arrayOf(PropTypes.string),
};

export default AddFilterDrawer;
